// Single source for turning a plan's *ticked pages* + limits into the marketing
// pricing bullets — so the public pricing page and the live access gate can never
// drift. The admin ticks pages in Plan Config; those same ticks (rolled up to a
// coarse, marketing-friendly module level) become the "what's included" list.
//
// Consumed by the pricing page (MarketingHomePage). The rollup mapping lives here
// (frontend) rather than in SQL because the page taxonomy already lives in the
// frontend page registry; the public reader RPC only ships the raw page_keys.

export interface PlanLimits {
  maxLocations: number
  maxProducts: number
}

// Human bullet for a numeric limit. -1 = unlimited (per subscription_plans convention).
function locationLimitLabel(n: number): string {
  if (n === -1) return 'Unlimited locations'
  if (n === 1) return '1 location'
  return `Up to ${n.toLocaleString('en-NG')} locations`
}

function productLimitLabel(n: number): string {
  if (n === -1) return 'Unlimited products'
  if (n === 1) return '1 product'
  return `Up to ${n.toLocaleString('en-NG')} products`
}

// The module-level bullets for a plan, derived ONLY from its ticked pages, in display
// order. Plumbing pages (dashboard, business settings, system) and the storefront/commerce
// surface (its own add-on column) are deliberately NOT surfaced as tier bullets.
//
// Two bullets are computed rather than a flat prefix match:
//   • Contacts + Expenses collapse into one "Customers, suppliers & expenses" bullet.
//   • The reporting/finance bullet escalates with what the tier actually includes:
//       financial statements + tax compliance → the IFRS + tax-computation standout;
//       financial statements only            → "Financial statements & reports";
//       any other reporting pages            → "Reports & analytics".
export function modulesFromPages(pageKeys: string[]): string[] {
  const set = new Set(pageKeys)
  const has = (pred: (k: string) => boolean): boolean => {
    for (const k of set) if (pred(k)) return true
    return false
  }
  const out: string[] = []

  if (has(k => k.startsWith('sell.'))) out.push('Point of sale & sales')
  if (has(k => k.startsWith('buy.'))) out.push('Purchases & suppliers')
  if (has(k => k.startsWith('inventory.'))) out.push('Inventory & stock control')
  if (has(k => k.startsWith('contacts.') || k.startsWith('expenses.'))) {
    out.push('Customers, suppliers & expenses')
  }
  if (has(k => k === 'trading.sales_per_rep' || k.startsWith('trading.incentive'))) {
    out.push('Sales reps & incentives')
  }
  if (has(k => k.startsWith('payroll.'))) out.push('Payroll (PAYE, pension, NHF)')
  // Accounting/GL — tax-compliance pages are excluded here; they feed the standout below.
  if (has(k => k.startsWith('accounting.') || (k.startsWith('finance.') && !k.startsWith('finance.tax_compliance.')))) {
    out.push('Accounting')
  }

  const hasFinStatements = set.has('reporting.financial_statements')
  const hasTaxCompliance = has(k => k.startsWith('finance.tax_compliance.'))
  const hasReporting = has(k => k.startsWith('reporting.'))
  if (hasFinStatements && hasTaxCompliance) {
    out.push('IFRS financial statements & tax computation (VAT, WHT, PAYE, CIT)')
  } else if (hasFinStatements) {
    out.push('Financial statements & reports')
  } else if (hasReporting) {
    out.push('Reports & analytics')
  }

  // School & Logistics are separate standalone products (HIDDEN_MODULE_CODES) and are
  // never advertised on the public pricing page, even if a plan's pages include them.

  return out
}

// The full inclusions list shown on a pricing column:
//   • if `override` has entries, it wins verbatim (admin polish of the wording);
//   • otherwise auto-generate: module bullets (from ticks) + the two limits.
// `override` maps to the plan's (now optional) free-text list — empty = auto.
export function derivePlanInclusions(
  pageKeys: string[],
  limits: PlanLimits,
  override?: string[] | null,
): string[] {
  const clean = (override ?? []).map(s => String(s).trim()).filter(Boolean)
  if (clean.length > 0) return clean

  return [
    ...modulesFromPages(pageKeys),
    locationLimitLabel(limits.maxLocations),
    productLimitLabel(limits.maxProducts),
  ]
}
